export const ELEMENT_OPTIMIZATION_MODE_OPTIONS = [
  { value: "automatic", label: "Automatic" },
  { value: "automatic_large", label: "Automatic Large" },
  { value: "none", label: "None" },
  { value: "custom", label: "Custom" },
];

export const ELEMENT_OPTIMIZATION_METHOD_OPTIONS = [
  { value: "remesh", label: "Remesh" },
  { value: "decimate", label: "Decimate" },
];

const DEFAULT_ELEMENT_OPTIMIZATION_FORM = Object.freeze({
  mode: "automatic",
  method: "remesh",
  targetFaces: "20000",
});

export function createDefaultElementOptimizationForm() {
  return { ...DEFAULT_ELEMENT_OPTIMIZATION_FORM };
}

export function createElementOptimizationFormFromPayload(optimization = {}) {
  const mode = normalizeOptimizationMode(optimization.mode);

  if (mode !== "custom") {
    return {
      ...DEFAULT_ELEMENT_OPTIMIZATION_FORM,
      mode,
    };
  }

  return {
    mode,
    method: normalizeOptimizationMethod(optimization.method),
    targetFaces: String(
      parseOptimizationTargetFaces(optimization.targetFaces) ||
        DEFAULT_ELEMENT_OPTIMIZATION_FORM.targetFaces
    ),
  };
}

export function isCustomElementOptimizationMode(mode) {
  return String(mode || "").trim().toLowerCase() === "custom";
}

export function parseOptimizationTargetFaces(value) {
  const parsed = Number.parseInt(String(value || "").trim(), 10);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : null;
}

export function isValidElementOptimizationForm(optimization) {
  if (!isCustomElementOptimizationMode(optimization?.mode)) {
    return true;
  }

  return (
    isSupportedOptimizationMethod(optimization?.method) &&
    parseOptimizationTargetFaces(optimization?.targetFaces) !== null
  );
}

export function buildElementOptimizationPayload(optimization = {}) {
  const mode = normalizeOptimizationMode(optimization.mode);

  if (mode !== "custom") {
    return { mode };
  }

  return {
    mode,
    method: normalizeOptimizationMethod(optimization.method),
    targetFaces: parseOptimizationTargetFaces(optimization.targetFaces) || 20000,
  };
}

export function getElementOptimizationModeHelperText(mode) {
  switch (normalizeOptimizationMode(mode)) {
    case "automatic_large":
      return "Use the larger automatic optimization profile when preprocessing is needed.";
    case "none":
      return "Skip preprocessing and send the source file directly to the build pipeline.";
    case "custom":
      return "Force preprocessing with the method and target faces below.";
    case "automatic":
    default:
      return "Use the default preprocessing heuristics and automatic optimization profile.";
  }
}

function normalizeOptimizationMode(value) {
  const normalized = String(value || "").trim().toLowerCase();

  if (
    normalized === "automatic" ||
    normalized === "automatic_large" ||
    normalized === "none" ||
    normalized === "custom"
  ) {
    return normalized;
  }

  return DEFAULT_ELEMENT_OPTIMIZATION_FORM.mode;
}

function normalizeOptimizationMethod(value) {
  return isSupportedOptimizationMethod(value)
    ? String(value).trim().toLowerCase()
    : DEFAULT_ELEMENT_OPTIMIZATION_FORM.method;
}

function isSupportedOptimizationMethod(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "remesh" || normalized === "decimate";
}
